// Implement a Firestore data provider for React Admin

import {
  GET_LIST,
  GET_ONE,
  CREATE,
  UPDATE,
  UPDATE_MANY,
  DELETE,
  DELETE_MANY,
  GET_MANY,
  GET_MANY_REFERENCE,
} from "react-admin"
// eslint-disable-next-line import/no-unresolved
import { DocumentSnapshot } from "@firebase/firestore-types"
import {pickBy, omitBy, get, replace} from "lodash"

import {
  getCountFromServer,
  collection,
  collectionGroup,
  query as v9Query,
  where,
  orderBy,
  endAt,
} from "@firebase/firestore"

import firebase, { dbV9 } from "../firebase"
import getTable from "./getTable"
import normalizeTimestamps, {
  normTimestamps,
  normTimestampsInList,
} from "../utils/normalizeTimestamps"


const collectionGroupTables = ["claims", "documentUploads"]

/**
 * Firestore has no offset: skipping to a page by reading and discarding the
 * preceding documents bills every one of them. We remember the last document
 * of each page that was actually fetched, so sequential paging continues from
 * a cursor and costs nothing extra. Jumping far ahead still has to skip, but
 * only from the closest cursor we already know.
 */
const MAX_CACHED_PAGINATIONS = 20
const pageCursors = new Map<string, any[]>()

function getPageCursors (cacheKey: string): any[] {
  return pageCursors.get(cacheKey) || []
}

function rememberPageCursor (cacheKey: string, page: number, doc: any) {
  if (!doc) {
    return
  }
  if (!pageCursors.has(cacheKey) &&
    pageCursors.size >= MAX_CACHED_PAGINATIONS) {
    pageCursors.clear()
  }
  const cursors = getPageCursors(cacheKey)
  cursors[page - 1] = doc
  pageCursors.set(cacheKey, cursors)
}

// Initialize Cloud Firestore through Firebase
const db = firebase.firestore()
// eslint-disable-next-line import/no-named-as-default-member
const auth = firebase.auth()


/**
 * Utility function to flatten firestore objects,
 * since 'id' is not a field in FireStore
 *
 * @param {DocumentSnapshot} docSnap Firestore document snapshot
 * @returns {Object} the DocumentSnapshot.data()
 * with an additionnal "Id" attribute
 */
function getDataWithId (docSnap: DocumentSnapshot) {
  let dataWithId = {}
  if (docSnap && docSnap.exists) {
    dataWithId = {
      id: docSnap.id,
      ...docSnap.data(),
    }
  }
  return dataWithId
}

function filterExistsData (
  existsFilters: Record<string, unknown>,
  filter: any,
  docSnap: any,
) {
  return docSnap.filter((doc: any) => {
    for (const key in existsFilters) {
      if (!filter[key]) {
        continue
      }

      const fieldName = key.split("__exists")[0]

      return doc.data()[fieldName]
    }

    return doc
  })
}

function filterNotEqualityData (
  notEqualityFilters: Record<string, unknown>,
  filter: any,
  docSnap: any,
) {
  return docSnap.filter((doc: any) => {
    for (const key in notEqualityFilters) {
      if (!filter[key]) {
        continue
      }

      const fieldName = key.split("__not")[0]
      
      if (doc.data()[fieldName] !== notEqualityFilters.type__not) {
        return true
      }
      continue
    }
    return false
  })
}

function filterRangeData (
  rangeFilters: Record<string, unknown>,
  filter: any,
  docSnap: any,
) {
  return docSnap.filter((doc: any) => {
    // eslint-disable-next-line guard-for-in
    for (const key in rangeFilters) {
      if (!filter[key]) {
        continue
      }

      const range = filter[key]
      const fieldName = key.split("__date-range")[0]
      
      const docValue = doc.data()[fieldName]
      if (!docValue || !docValue.seconds) {
        return false
      }

      const startDate: any = new Date(range.startDate)
      const endDate: any = new Date(range.endDate)

      const docTime = docValue.seconds * 1000
      
      if (docTime < startDate.getTime() || docTime > endDate.getTime()) {
        return false
      }
    }

    return true
  })
}

/**
 * Flattens nested filter objects into dot notation format 
 * Example: { bmwCarData: { currentMileage__gte: 1000 } }
 * becomes { "bmwCarData.currentMileage__gte": 1000 }
 * @param {Object} filter The filter object to flatten
 * @returns {Object} The flattened filter object
 */
function flattenFilterObject (filter: Record<string, any>):Record<string, any> {
  const result: Record<string, any> = {}
  
  function flatten (obj: Record<string, any>, prefix = "") {
    for (const key in obj) {
      if ((!obj[key])) {
        continue
      }

      const value = obj[key]
      const newKey = prefix ? `${prefix}.${key}` : key
      
      // If the value is an object and not null,
      // and not a special filter object (like date range)
      if ( typeof value === "object" && value !== null &&
        !Array.isArray(value) && !key.includes("__date-range") &&
        !("startDate" in value && "endDate" in value)
      ) {
        // Recursively flatten the nested object
        flatten(value, newKey)
      }
      else {
        // Add the key-value pair to the result
        result[newKey] = value
      }
    }
  }
  
  flatten(filter)
  return result
}


/**
 * Maps react-admin queries to Firebase
 *
 * @param {string} type Request type, e.g GET_LIST
 * @param {string} resource Resource name, e.g. "posts"
 * @param {Object} payload Request parameters. Depends on the request type
 * @returns {Promise} the Promise for a data response
 */
export async function firestoreProvider (
  type: string,
  resource: string,
  params: {
    pagination: any
    sort: any
    filter: any
    id: string
    ids: string[]
    data: any
    previousData: any
    target: any
  },
) {
  const resourceTableName = getTable(resource)
  const usingCollectionGroup = collectionGroupTables
    .includes(resourceTableName)
  console.info("Making", type, resourceTableName, params, usingCollectionGroup)

  const initialQuery = usingCollectionGroup
    ? db.collectionGroup(resourceTableName)
    : db.collection(resourceTableName)

  switch (type) {
    case GET_LIST: {
      const { page, perPage } = params.pagination
      const { field, order } = params.sort
      const filter = flattenFilterObject(params.filter)
      let hasLtFilters = false
      let hasGtFilters = false
      let hasLteFilters = false
      let hasGteFilters = false
      let hasExistTrueFilters = false
      let hasMoreRangeFilters = false
      let hasNotEqualityFilters = false
      let hasBooleanSelectFilters = false

      const rangeFilters = pickBy(
        filter,
        (v, k) => k.endsWith("__date-range"),
      )
      const existsFilters = pickBy(
        filter,
        (v, k) => k.endsWith("__exists"),
      )

      const notEqualityFilters = pickBy(
        filter,
        (v, k) => k.endsWith("__not"),
      )

      const booleanFilters = pickBy(
        filter,
        (v, k) => ["__true__", "__false__"].includes(v),
      )

      // query all the docs from the first to page*perPage
      let query = initialQuery
      const v9Conditions = []

      if (filter) {
        const ltFilters = pickBy(
          filter,
          (v, k) => k.endsWith("__lt"),
        )
        const lteFilters = pickBy(
          filter,
          (v, k) => k.endsWith("__lte"),
        )
        const gtFilters = pickBy(
          filter,
          (v, k) => k.endsWith("__gt"),
        )
        const gteFilters = pickBy(
          filter,
          (v, k) => k.endsWith("__gte"),
        )
        const equalityFilters = omitBy(
          filter,
          (v, k) =>
            k.endsWith("__date-range") ||
            k.endsWith("__lt") ||
            k.endsWith("__lte") ||
            k.endsWith("__gt") ||
            k.endsWith("__gte") ||
            k.endsWith("__exists") ||
            k.endsWith("__not") ||
            ["__true__", "__false__"].includes(v),
        )

        if (Object.keys(ltFilters).length !== 0) {
          hasLtFilters = true
        }
        if (Object.keys(gtFilters).length !== 0) {
          hasGtFilters = true
        }
        if (Object.keys(lteFilters).length !== 0) {
          hasLteFilters = true
        }
        if (Object.keys(gteFilters).length !== 0) {
          hasGteFilters = true
        }
        if (Object.keys(booleanFilters).length !== 0) {
          hasBooleanSelectFilters = true
        }

        query = Object.keys(equalityFilters)
          .reduce((q, k) => {
            const value = filter[k]

            if (k.indexOf("__custom-field-value") > -1) {
              const filterKey = replace(k, "__custom-field-value", "")
              k = `customFieldValues.${filterKey}`
            }

            if (k.indexOf("__case") > -1) {
              const filterKey = replace(k, "__case", "")
              k = `case.${filterKey}`
            }

            if (k.indexOf("__number") > -1) {
              const filterKey = replace(k, "__number", "")
              v9Conditions.push(where(filterKey, "==", Number(value)))
              return q.where(filterKey, "==", Number(value))
            }

            if (value === "__true__") {
              return q.where(k, "==", true)
            }
            else if (value === "__false__") {
              return q.where(k, "==", false)
            }

            v9Conditions.push(where(k, "==", value))

            return q.where(k, "==", value)
          }, query)

        let cntRangeFilters = 0

        query = Object.keys(rangeFilters).reduce((q, k) => {
          cntRangeFilters++
          const range = filter[k]
          if (!range.startDate || !range.endDate) {
            return query
          }

          const fieldName = k.split("__date-range")[0] as string

          if ((cntRangeFilters > 1 && Object.keys(rangeFilters).length !== 0)) {
            hasMoreRangeFilters = true
            return query
          }

          v9Conditions.push(where(fieldName, ">=", new Date(range.startDate)))
          v9Conditions.push(where(fieldName, "<=", new Date(range.endDate)))

          if (fieldName !== "createdTime") {
            v9Conditions.push(orderBy(fieldName))

            return q
              .where(fieldName, ">=", new Date(range.startDate))
              .where(fieldName, "<=", new Date(range.endDate))
              .orderBy(fieldName)
          }

          return q
            .where(fieldName, ">=", new Date(range.startDate))
            .where(fieldName, "<=", new Date(range.endDate))
        }, query)

        query = Object.keys(ltFilters).reduce((q, k) => {
          const fieldName = k.split("__lt")[0] as string

          v9Conditions.push(orderBy(fieldName))
          v9Conditions.push(orderBy(field, order.toLowerCase()))
          v9Conditions.push(endAt(filter[k]))

          return q
            .orderBy(fieldName)
            .orderBy(field, order.toLowerCase())
            .endAt(filter[k])
        }, query)

        query = Object.keys(lteFilters).reduce((q, k) => {
          const fieldName = k.split("__lte")[0] as string
          v9Conditions.push(where(fieldName, "<=", filter[k]))

          return q
            .where(fieldName, "<=", filter[k])
        }, query)

        query = Object.keys(gtFilters).reduce((q, k) => {
          const fieldName = k.split("__gt")[0] as string

          v9Conditions.push(where(fieldName, ">", filter[k]))

          return q
            .where(fieldName, ">", filter[k])
        }, query)

        query = Object.keys(gteFilters).reduce((q, k) => {
          const fieldName = k.split("__gte")[0] as string
          v9Conditions.push(where(fieldName, ">=", filter[k]))

          return q
            .where(fieldName, ">=", filter[k])
        }, query)

        query = Object.keys(existsFilters).reduce((q, k) => {
          const fieldName = k.split("__exists")[0]

          const rangeKey = `${fieldName}__date-range`
          if (rangeFilters[rangeKey] && rangeFilters[rangeKey].startDate &&
            rangeFilters[rangeKey].endDate) {
            return q
          }

          if (!filter[k]) {
            v9Conditions.push(where(fieldName, "==", null))

            return q
              .where(fieldName, "==", null)
          }

          hasExistTrueFilters = true

          const epochStart = new Date(0)
          v9Conditions.push(where(fieldName, ">", epochStart))
          v9Conditions.push(orderBy(fieldName))
          
          return q
            .where(fieldName, ">", epochStart)
            .orderBy(fieldName)
        }, query)

        query = Object.keys(notEqualityFilters).reduce((q, k) => {
          const fieldName = k.split("__not")[0] as string
          
          const value = filter[k]

          if (!value) {
            return q.where(fieldName, "==", null)
          }

          v9Conditions.push(
            orderBy(field, order.toLowerCase()),
          )
          hasNotEqualityFilters = true
          return q
            .orderBy(field, order.toLowerCase())
        }, query)
      }

      query = Object.keys(booleanFilters).reduce((q, k) => {
        const fieldName = k as string
        if (filter[k] === "__true__") {
          v9Conditions.push(where(fieldName, "==", true))
          return q.where(fieldName, "==", true)
        }
        else if (filter[k] === "__false__") {
          v9Conditions.push(where(fieldName, "==", false))
          return q.where(fieldName, "==", false)
        }
        return q
      }, query)

      if (field !== "id" && !hasLtFilters && !hasGtFilters && !hasLteFilters &&
        !hasGteFilters && !hasNotEqualityFilters && !hasBooleanSelectFilters &&
        !hasExistTrueFilters) {
        query = query.orderBy(field, order.toLowerCase())
        v9Conditions.push(orderBy(field, order.toLowerCase()))
      }

      // When client-side filters are needed, we must overfetch
      const needsClientFilter = hasNotEqualityFilters || hasMoreRangeFilters

      const cacheKey = JSON.stringify([
        resourceTableName,
        usingCollectionGroup,
        params.filter,
        field,
        order,
        perPage,
      ])

      let queryDocs: any[]
      if (needsClientFilter) {
        // Must overfetch for client-side filtering
        queryDocs = (await query.limit(page * perPage).get()).docs
      }
      else {
        if (page > 1) {
          const cursors = getPageCursors(cacheKey)

          // Closest page we already hold a cursor for
          let resumeFromPage = 1
          let cursor = null
          for (let p = page - 1; p >= 1; p--) {
            if (cursors[p - 1]) {
              cursor = cursors[p - 1]
              resumeFromPage = p + 1
              break
            }
          }

          if (cursor) {
            query = query.startAfter(cursor)
          }

          // Only the gap between the cursor and the wanted page needs skipping
          const docsToSkip = (page - resumeFromPage) * perPage
          if (docsToSkip > 0) {
            const skipDocs = await query.limit(docsToSkip).get()
            const lastDoc = skipDocs.docs[skipDocs.docs.length - 1]
            if (lastDoc) {
              query = query.startAfter(lastDoc)
            }
          }
        }
        queryDocs = (await query.limit(perPage).get()).docs
        rememberPageCursor(cacheKey, page, queryDocs[queryDocs.length - 1])
      }

      // Skipped when a client-side filter is active, because the count would
      // be discarded in favour of queryDocs.length below. count() bills one
      // read per 1000 matched index entries, so asking for it was never free.
      let serverTotal = 0
      if (!needsClientFilter) {
        const v9FilterQuery = v9Query(usingCollectionGroup
          ? collectionGroup(dbV9, resourceTableName)
          : collection(dbV9, resourceTableName), ...v9Conditions)
        serverTotal = (await getCountFromServer(v9FilterQuery)).data().count
      }

      if (hasNotEqualityFilters) {
        queryDocs = filterNotEqualityData(notEqualityFilters, filter, queryDocs)
      }

      if (hasMoreRangeFilters) {
        queryDocs = filterRangeData(rangeFilters, filter, queryDocs)
      }

      // Slice only when client-side filtering was used
      const pageData = needsClientFilter
        ? queryDocs.slice((page - 1) * perPage, page * perPage)
        : queryDocs

      return {
        data: normTimestampsInList(
          pageData.map((doc: any) => getDataWithId(doc)),
        ),
        total: needsClientFilter
          ? queryDocs.length : serverTotal,
      }
    }

    case GET_ONE: {
      if (usingCollectionGroup) {
        return initialQuery
          .where("id", "==", params.id)
          .limit(1)
          .get()
          .then(querySnap => {
            if (querySnap.docs.length > 0) {
              return {
                data: normTimestamps(getDataWithId(querySnap.docs[0])),
              }
            }
            return { data: null }
          })
          .catch(error => {
            return Promise.reject({ message: error.message, status: 404 })
          })
      }
      else {
        return initialQuery
          // TODO: Fix type error
          // @ts-expect-error
          .doc(params.id)
          .get()
          .then((doc: DocumentSnapshot) => {
            if (doc.exists) {
              return {
                data: normTimestamps(getDataWithId(doc)),
              }
            }
            else {
              throw new Error("No such doc")
            }
          })
          .catch((error: Error) => {
            return Promise.reject({ message: error.message, status: 404 })
          })
      }
    }

    case UPDATE: {
      if (typeof params.data === "object") {
        normalizeTimestamps(params.data)
      }

      if (usingCollectionGroup) {
        console.info("Start collection group update")
        return initialQuery
          .where("id", "==", params.id)
          .limit(1)
          .get()
          .then((querySnap) => {
            console.info(querySnap.docs)
            if (querySnap.size > 0) {
              const updateObj = {
                ...params.data,
                updatedBy: "admin-dashboard",
                updatedById: get(auth, "currentUser.uid", null),
              }

              return querySnap.docs[0].ref
                .update(updateObj)
                .then(() => ({ data: params.data }))
            }
            else {
              console.error("No data found for id", params.id)
            }
          })
      }

      console.info("collection update")
      return initialQuery
        // TODO: Fix type error
        // @ts-expect-error
        .doc(params.id)
        .set({
          ...params.data,
          updatedBy: "admin-dashboard",
          updatedById: get(auth, "currentUser.uid", null),
        })
        .then(() => {
          return { data: params.data }
        })
    }
    case CREATE: {
      if (typeof params.data === "object") {
        normalizeTimestamps(params.data)
      }

      if (usingCollectionGroup) {
        console.error("CREATE not implemented with a collection group")
      }

      // remove a reference if it exists
      return db
        .collection(resourceTableName)
        .add({
          ...params.data,
          updatedBy: "admin-dashboard",
          updatedById: get(auth, "currentUser.uid", null),
        })
        .then(DocumentReference =>
          DocumentReference
            .get()
            .then(DocumentSnapshot => {
              return { data: getDataWithId(DocumentSnapshot) }
            }),
        )
    }

    case UPDATE_MANY: {
      if (typeof params.data === "object") {
        normalizeTimestamps(params.data)
      }
      if (usingCollectionGroup) {
        console.error("UPDATE_MANY not implemented on a collection group")
      }
      return params.ids.map((id: string) =>
        initialQuery
          // @ts-expect-error
          .doc(id)
          .set({
            ...params.data,
            updatedBy: "admin-dashboard",
            updatedById: get(auth, "currentUser.uid", null),
          })
          .then(() => id),
      )
    }

    case DELETE: {
      if (usingCollectionGroup) {
        return db.collectionGroup(resourceTableName)
          .where("id", "==", params.id)
          .limit(1)
          .get()
          .then((querySnap) => {
            if (querySnap.size > 0) {
              return querySnap.docs[0].ref.delete()
                .then(() => ({ data: params.previousData }))
            }
            else {
              return { data: params.previousData }
            }
          })
      }
      return initialQuery
        // @ts-expect-error
        .doc(params.id)
        .delete()
        .then(() => {
          return { data: params.previousData }
        })
    }

    case DELETE_MANY: {
      if (usingCollectionGroup) {
        console.info("params", params)
        const tasks = params.ids.map((id: string) =>
          db.collectionGroup(resourceTableName)
            .where("id", "==", id)
            .limit(1)
            .get()
            .then(querySnap =>
              querySnap.docs.map(snap =>
                snap.ref.delete(),
              ),
            ),
        )
        return Promise.all(tasks).then(() => ({ data: params.ids }))
      }
      return {
        data: params.ids.map((id: string) =>
          db
            .collection(resourceTableName)
            .doc(id)
            .delete()
            .then(() => id),
        ),
      }
    }

    case GET_MANY: {
      // Do not use FireStore Ref
      // because react-admin will not be able to create or update
      // Use a String field containing the ID instead
      if (usingCollectionGroup) {
        return Promise
          .all(
            params.ids.map((id: string) => {
              return initialQuery.where("id", "==", id).limit(1).get()
            }),
          ).then(querySnaps => {
            return querySnaps.map((snap: any) =>
              snap.size > 0 ? snap.docs[0] : null)
          })
          .then(arrayOfResults =>
            ({ data: arrayOfResults.map(documentSnapshot =>
              getDataWithId(documentSnapshot)),
            }),
          )
      }
      else {
        return Promise
          .all(params.ids.map((id: string) => {
            return initialQuery
              // @ts-expect-error
              .doc(id)
              .get()
          }))
          .then(arrayOfResults => {
            return {
              data: arrayOfResults.map((documentSnapshot: any) =>
                getDataWithId(documentSnapshot)),
            }
          })
      }
    }

    case GET_MANY_REFERENCE: {
      console.info("Get many ref", params)
      const { target, id } = params
      const { field, order } = params.sort
      // FIXME: unbounded - reads every matching document. Needs real
      // pagination in the calling ReferenceManyField rather than a silent
      // limit here, which would hide records from case workers.
      return initialQuery
        .where(target, "==", id)
        .orderBy(field, order.toLowerCase())
        .get()
        .then(QuerySnapshot => {
          return {
            data: QuerySnapshot.docs.map(DocumentSnapshot =>
              getDataWithId(DocumentSnapshot)),
            total: QuerySnapshot.docs.length,
          }
        })
    }

    default: {
      throw new Error(`Unsupported Data Provider request type ${type}`)
    }
  }
}
